import React from 'react';
import classNames from 'classnames';
import { FormattedMessage } from '../../util/reactIntl';
import { ReviewRating } from '../../components';
import { REVIEW_TYPE_OF_PROVIDER, REVIEW_TYPE_OF_CUSTOMER } from '../../util/types';

import css from './UserReviewsSummary.module.css';

/**
 * A component that displays a summary of user reviews (average rating and count)
 *
 * @component
 * @param {Object} props
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {Array} props.reviews - Array of review objects
 * @param {string} [props.reviewType] - Type of reviews to show ('ofProvider' or 'ofCustomer')
 * @param {boolean} [props.loading] - Whether reviews are being loaded
 * @returns {JSX.Element}
 */
const UserReviewsSummary = props => {
  const { className, rootClassName, reviews = [], reviewType = REVIEW_TYPE_OF_PROVIDER, loading = false } = props;
  const classes = classNames(rootClassName || css.root, className);

  // Filter reviews by type if specified
  const filteredReviews = reviewType 
    ? reviews.filter(review => review.attributes.type === reviewType)
    : reviews;

  // If no reviews of the specified type, show all reviews as fallback
  const reviewsToShow = filteredReviews.length > 0 ? filteredReviews : reviews;

  // Calculate average rating
  const reviewsWithRating = reviewsToShow.filter(review => review.attributes.rating);
  const totalRating = reviewsWithRating.reduce((sum, review) => sum + review.attributes.rating, 0);
  const averageRating = reviewsWithRating.length > 0 ? totalRating / reviewsWithRating.length : 0;

  return (
    <div className={classes}>
      <div className={css.ratingContainer}>
        {loading ? (
          <span className={css.loading}>Loading reviews...</span>
        ) : (
          <>
            <ReviewRating
              rating={Math.round(averageRating)}
              className={css.rating}
              reviewStarClassName={css.reviewStar}
            />
            <span className={css.reviewCount}>
              <FormattedMessage 
                id="UserReviewsSummary.reviewCount" 
                values={{ count: reviewsToShow.length }} 
              />
            </span>
          </>
        )}
      </div>
    </div>
  );
};

export default UserReviewsSummary; 